import type { EconomicEvent, BondYield, CreditSpread, EconomicEventCategory } from '@/types';
import { hashString } from '@/utils/format';

// ---------------------------------------------------------------------------
// 실시간 경제 캘린더 — ForexFactory 주간 JSON (/api/econ-calendar 프록시)
// 실패 시 아래 정적 ECONOMIC_CALENDAR 폴백
// ---------------------------------------------------------------------------

interface FFEvent {
  title: string;
  country: string;   // 통화코드 (USD/EUR/JPY...)
  date: string;      // ISO with TZ
  impact: string;    // High | Medium | Low | Holiday
  forecast: string;
  previous: string;
}

const CURRENCY_COUNTRY: Record<string, { name: string; code: string }> = {
  USD: { name: 'USA', code: 'US' },
  EUR: { name: 'EU', code: 'EU' },
  GBP: { name: 'UK', code: 'GB' },
  JPY: { name: 'Japan', code: 'JP' },
  CNY: { name: 'China', code: 'CN' },
  KRW: { name: 'South Korea', code: 'KR' },
  AUD: { name: 'Australia', code: 'AU' },
  NZD: { name: 'New Zealand', code: 'NZ' },
  CAD: { name: 'Canada', code: 'CA' },
  CHF: { name: 'Switzerland', code: 'CH' },
};

function inferCategory(title: string): EconomicEventCategory {
  if (/rate decision|interest rate|fomc|cash rate|refinancing/i.test(title)) return 'interest-rate';
  if (/cpi|ppi|inflation|price index/i.test(title)) return 'inflation';
  if (/gdp/i.test(title)) return 'gdp';
  if (/employment|unemployment|payroll|claims|jobs|labor/i.test(title)) return 'employment';
  if (/trade|export|import|balance|tariff/i.test(title)) return 'trade';
  if (/pmi|manufacturing index|ism/i.test(title)) return 'pmi';
  if (/election|vote/i.test(title)) return 'election';
  return 'sentiment';
}

let _liveCache: EconomicEvent[] | null = null;
let _liveCacheTs = 0;

export async function fetchLiveCalendar(): Promise<EconomicEvent[]> {
  const now = Date.now();
  if (_liveCache && now - _liveCacheTs < 30 * 60_000) return _liveCache;

  try {
    const res = await fetch('/api/econ-calendar', { signal: AbortSignal.timeout(20_000) });
    if (!res.ok) throw new Error(`econ-calendar ${res.status}`);
    const raw = (await res.json()) as FFEvent[];
    if (!Array.isArray(raw) || raw.length === 0) throw new Error('empty calendar');

    const events = raw
      .filter((e) => e.impact !== 'Holiday' && e.title)
      .map((e): EconomicEvent => {
        const d = new Date(e.date);
        const cc = CURRENCY_COUNTRY[e.country] ?? { name: e.country, code: e.country.slice(0, 2) };
        return {
          id: `ec-live-${hashString(`${e.title}${e.date}`)}`,
          title: `${e.country} ${e.title}`,
          country: cc.name,
          countryCode: cc.code,
          date: d.toISOString().slice(0, 10),
          time: d.toISOString().slice(11, 16),
          importance: e.impact === 'High' ? 'high' : e.impact === 'Medium' ? 'medium' : 'low',
          previous: e.previous || undefined,
          forecast: e.forecast || undefined,
          category: inferCategory(e.title),
          impact: 'neutral',
        };
      });

    _liveCache = events;
    _liveCacheTs = now;
    console.log(`[EconCal] ${events.length} live events loaded`);
    return events;
  } catch (err) {
    console.warn('[EconCal] live fetch failed, using static fallback:', err);
    return ECONOMIC_CALENDAR;
  }
}

// ---------------------------------------------------------------------------
// Economic Calendar + Bond Yields — Bloomberg Terminal style
// ---------------------------------------------------------------------------

// Date helper — relative to 2026-03-07
const BASE = new Date('2026-03-07');
function dateOffset(days: number): string {
  const d = new Date(BASE);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function dailySeed(offset: number = 0): number {
  const d = new Date();
  return Math.sin((d.getFullYear() * 365 + d.getMonth() * 31 + d.getDate() + offset) * 2.71828);
}

// ---------------------------------------------------------------------------
// Economic Calendar Events (next 14 days)
// ---------------------------------------------------------------------------
export const ECONOMIC_CALENDAR: EconomicEvent[] = [
  { id: 'ec-fomc-1', title: 'FOMC Interest Rate Decision', country: 'USA', countryCode: 'US', date: dateOffset(1), time: '18:00', importance: 'high', previous: '4.50%', forecast: '4.50%', category: 'interest-rate', impact: 'neutral' },
  { id: 'ec-cpi-us', title: 'US CPI YoY (Feb)', country: 'USA', countryCode: 'US', date: dateOffset(1), time: '13:30', importance: 'high', previous: '3.1%', forecast: '3.2%', category: 'inflation', impact: 'bearish' },
  { id: 'ec-nfp', title: 'US Non-Farm Payrolls (Feb)', country: 'USA', countryCode: 'US', date: dateOffset(3), time: '13:30', importance: 'high', previous: '256K', forecast: '185K', category: 'employment', impact: 'neutral' },
  { id: 'ec-ecb', title: 'ECB Rate Decision', country: 'EU', countryCode: 'EU', date: dateOffset(5), time: '13:15', importance: 'high', previous: '2.75%', forecast: '2.50%', category: 'interest-rate', impact: 'bullish' },
  { id: 'ec-gdp-eu', title: 'Eurozone GDP QoQ (Q4)', country: 'EU', countryCode: 'EU', date: dateOffset(5), time: '10:00', importance: 'high', previous: '0.1%', forecast: '0.2%', category: 'gdp', impact: 'bullish' },
  { id: 'ec-boj', title: 'Bank of Japan Rate Decision', country: 'Japan', countryCode: 'JP', date: dateOffset(8), time: '03:00', importance: 'high', previous: '0.25%', forecast: '0.50%', category: 'interest-rate', impact: 'bullish' },
  { id: 'ec-cpi-cn', title: 'China CPI YoY (Feb)', country: 'China', countryCode: 'CN', date: dateOffset(2), time: '01:30', importance: 'medium', previous: '-0.5%', forecast: '-0.3%', category: 'inflation', impact: 'neutral' },
  { id: 'ec-pmi-us', title: 'US Manufacturing PMI', country: 'USA', countryCode: 'US', date: dateOffset(0), time: '14:45', importance: 'medium', previous: '47.8', forecast: '48.5', category: 'pmi', impact: 'neutral' },
  { id: 'ec-pmi-eu', title: 'Eurozone Manufacturing PMI', country: 'EU', countryCode: 'EU', date: dateOffset(0), time: '09:00', importance: 'medium', previous: '45.9', forecast: '46.8', category: 'pmi', impact: 'bullish' },
  { id: 'ec-sanctions-ru', title: 'US OFAC Russia Sanctions Package (G7)', country: 'USA', countryCode: 'US', date: dateOffset(2), time: '15:00', importance: 'high', category: 'sanctions', impact: 'bearish' },
  { id: 'ec-election-kr', title: 'South Korea Presidential Election', country: 'South Korea', countryCode: 'KR', date: dateOffset(3), time: '06:00', importance: 'high', category: 'election', impact: 'neutral' },
  { id: 'ec-trade-us', title: 'US Trade Balance (Jan)', country: 'USA', countryCode: 'US', date: dateOffset(7), time: '13:30', importance: 'medium', previous: '-$98.4B', forecast: '-$96.1B', category: 'trade', impact: 'neutral' },
  { id: 'ec-gdp-cn', title: 'China GDP QoQ (Q4 Final)', country: 'China', countryCode: 'CN', date: dateOffset(9), time: '02:00', importance: 'high', previous: '1.3%', forecast: '1.1%', category: 'gdp', impact: 'bearish' },
  { id: 'ec-rba', title: 'RBA Rate Decision', country: 'Australia', countryCode: 'AU', date: dateOffset(4), time: '03:30', importance: 'medium', previous: '4.10%', forecast: '3.85%', category: 'interest-rate', impact: 'bullish' },
  { id: 'ec-unemp-eu', title: 'Eurozone Unemployment Rate', country: 'EU', countryCode: 'EU', date: dateOffset(6), time: '10:00', importance: 'medium', previous: '6.2%', forecast: '6.1%', category: 'employment', impact: 'bullish' },
];

// ---------------------------------------------------------------------------
// Bond Yields (10 major sovereign bonds)
// ---------------------------------------------------------------------------
function synYield(base: number, offsetSeed: number): number {
  return +(base + dailySeed(offsetSeed) * 0.15).toFixed(3);
}
function synChange(offsetSeed: number): number {
  return +(dailySeed(offsetSeed + 100) * 0.08).toFixed(3);
}

const now = new Date().toISOString();

export const BOND_YIELDS: BondYield[] = [
  // US Treasuries
  { country: 'USA',         countryCode: 'US', maturity: '2Y',  yield: synYield(4.65, 1),  change: synChange(1),  timestamp: now },
  { country: 'USA',         countryCode: 'US', maturity: '10Y', yield: synYield(4.42, 2),  change: synChange(2),  timestamp: now },
  { country: 'USA',         countryCode: 'US', maturity: '30Y', yield: synYield(4.61, 3),  change: synChange(3),  timestamp: now },
  // Germany Bund
  { country: 'Germany',     countryCode: 'DE', maturity: '2Y',  yield: synYield(2.51, 4),  change: synChange(4),  timestamp: now },
  { country: 'Germany',     countryCode: 'DE', maturity: '10Y', yield: synYield(2.73, 5),  change: synChange(5),  timestamp: now },
  // Japan JGB
  { country: 'Japan',       countryCode: 'JP', maturity: '2Y',  yield: synYield(0.71, 6),  change: synChange(6),  timestamp: now },
  { country: 'Japan',       countryCode: 'JP', maturity: '10Y', yield: synYield(1.52, 7),  change: synChange(7),  timestamp: now },
  // China CGBs
  { country: 'China',       countryCode: 'CN', maturity: '10Y', yield: synYield(2.21, 8),  change: synChange(8),  timestamp: now },
  // UK Gilts
  { country: 'UK',          countryCode: 'GB', maturity: '10Y', yield: synYield(4.55, 9),  change: synChange(9),  timestamp: now },
  // Korea KTB
  { country: 'South Korea', countryCode: 'KR', maturity: '10Y', yield: synYield(2.98, 10), change: synChange(10), timestamp: now },
  // France OATs
  { country: 'France',      countryCode: 'FR', maturity: '10Y', yield: synYield(3.21, 11), change: synChange(11), timestamp: now },
  // Ukraine War Bonds (elevated)
  { country: 'Ukraine',     countryCode: 'UA', maturity: '10Y', yield: synYield(17.5, 12), change: synChange(12), timestamp: now },
];

// ---------------------------------------------------------------------------
// Credit Spreads
// ---------------------------------------------------------------------------
function synSpread(base: number, seed: number): number {
  return Math.round(base + dailySeed(seed) * 15);
}

export const CREDIT_SPREADS: CreditSpread[] = [
  { name: 'US IG Corporate',   spread: synSpread(105, 20), change: Math.round(dailySeed(120) * 5), description: 'Investment-grade corporate bond spread over Treasuries' },
  { name: 'US HY (Junk)',      spread: synSpread(345, 21), change: Math.round(dailySeed(121) * 12), description: 'High-yield (junk) bond spread — risk-on indicator' },
  { name: 'EM Sovereign (EMBIG)', spread: synSpread(420, 22), change: Math.round(dailySeed(122) * 18), description: 'Emerging market sovereign spread — geopolitical risk proxy' },
  { name: 'Ukraine Eurobond',  spread: synSpread(1250, 23), change: Math.round(dailySeed(123) * 40), description: 'Ukrainian sovereign bond spread — war risk premium' },
  { name: 'EU Periphery (IT-DE)', spread: synSpread(138, 24), change: Math.round(dailySeed(124) * 6), description: 'Italy–Germany spread — Eurozone fragmentation risk' },
];

export const IMPORTANCE_COLORS = {
  high:   'text-red-400',
  medium: 'text-amber-400',
  low:    'text-slate-500',
};

export const CATEGORY_ICONS: Record<string, string> = {
  'interest-rate': '🏦',
  'gdp':           '📊',
  'inflation':     '📈',
  'employment':    '👷',
  'trade':         '🚢',
  'pmi':           '🏭',
  'sentiment':     '😨',
  'sanctions':     '🚫',
  'election':      '🗳️',
};
